// In-visual filter builder — the ⋮ menu's "Filter…" dialog.
//
// Edits the FilterExpression a visual evaluates against its OWN loaded data
// (see visualFilter.ts). Every condition is one row — column / operator /
// value — combined with Match all (AND) or Match any (OR), which is the same
// shape the SQL-side criteria groups use, so "filter" means one thing across the
// product.
//
// Because the filtering is client-side over data already in the browser, the
// value input can offer the column's REAL distinct values as a picklist rather
// than making the user type a value and hope it matches.
//
// The vendored Plotly charts have their own equivalent popup inside BaseVisual
// (`_openFilterPopup`), so this dialog serves the NATIVE renderers (table /
// pivot / cards / feeds / calendar / tree / gantt / combo / map / html) — the
// visuals that had no in-visual filter at all.

import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { humanizeColumnLabel, mapVisualData, pickGroup } from './dataUtils'
import type { MappedColumn } from './dataUtils'
import { formatCell } from './formatters'
import { hasFilterRules, isFilterGroup } from './visualFilter'
import type { FilterCondition, FilterExpression, FilterOperator } from './visualFilter'
import type { VisualJsonData, VisualLayout } from './types'

/** One selectable value: the raw value matched against rows + its display text. */
export interface ValueOption { raw: unknown; display: string }

// Operators offered per column type. Text columns get the string tests, numbers
// and dates the ordered ones; every column gets equality, membership and the
// null tests.
const TEXT_OPS: FilterOperator[] = [
  '=', '!=', 'in', 'not in', 'contains', 'not contains', 'starts with', 'ends with',
  'is null', 'is not null',
]
const ORDERED_OPS: FilterOperator[] = [
  '=', '!=', 'in', 'not in', '>', '>=', '<', '<=', 'between', 'is null', 'is not null',
]

const OP_LABEL: Record<FilterOperator, string> = {
  '=': 'equals',
  '!=': 'does not equal',
  'in': 'is any of',
  'not in': 'is none of',
  '>': 'greater than',
  '>=': 'greater or equal',
  '<': 'less than',
  '<=': 'less or equal',
  'between': 'between',
  'contains': 'contains',
  'not contains': 'does not contain',
  'starts with': 'starts with',
  'ends with': 'ends with',
  'is null': 'is blank',
  'is not null': 'is not blank',
}

// Distinct values collected per column. The value editor is a COMBO BOX — you
// type or you pick — and typing narrows the list, so this can be far larger than
// a plain <select> could usefully be. It is still bounded: the list is built
// eagerly for every column, and an unbounded one on a 5k-row dataset is memory
// spent on a list nobody scrolls.
const MAX_PICKLIST = 1000
// Suggestions rendered at once. Beyond this the user is told to keep typing —
// painting a thousand rows to be scrolled past is slower AND less useful than
// narrowing.
const MAX_SUGGESTIONS = 50

const BORDER = '1px solid var(--color-border, #d6dae0)'
const FONT = '12px "Open Sans",Arial,sans-serif'
const ACCENT = 'var(--pg-primary, #1976d2)'

/** Accent wash painted over an opaque surface colour, so it tints without going
 *  see-through. `--pg-primary-soft` is defined by no host stylesheet, so the
 *  bare `rgba(…, 0.08)` fallback it used to carry let whatever sat behind the
 *  control bleed through the chip and the highlighted row. Longhands, not the
 *  `background` shorthand — see the note on QuickFilterBar.softAccent. */
function softAccent(alpha: number): CSSProperties {
  const wash = `rgba(25,118,210,${alpha})`
  return {
    backgroundColor: 'var(--color-bg, #fff)',
    backgroundImage: `linear-gradient(${wash}, ${wash})`,
  }
}

/** Suggestions matching what has been typed — substring, case-insensitive, over
 *  BOTH the display text and the raw value (a code list is searched by either).
 *  Exported for the regression tests. */
export function matchValueOptions(
  options: ValueOption[] | undefined,
  query: string,
  exclude: unknown[] = [],
): ValueOption[] {
  if (!options?.length) return []
  const q = query.trim().toLowerCase()
  const taken = new Set(exclude.map((v) => String(v ?? '')))
  const out: ValueOption[] = []
  for (const opt of options) {
    if (taken.has(String(opt.raw ?? ''))) continue
    if (q && !opt.display.toLowerCase().includes(q)
      && !String(opt.raw ?? '').toLowerCase().includes(q)) continue
    out.push(opt)
    if (out.length >= MAX_SUGGESTIONS) break
  }
  return out
}

function isOrdered(col: MappedColumn | undefined): boolean {
  const t = `${col?.type || ''} ${col?.coltype || ''}`.toLowerCase()
  return /number|int|dec|float|double|date|time/.test(t)
}

function opsFor(col: MappedColumn | undefined): FilterOperator[] {
  return isOrdered(col) ? ORDERED_OPS : TEXT_OPS
}

function operandCount(op: FilterOperator): number {
  if (op === 'is null' || op === 'is not null') return 0
  if (op === 'between') return 2
  return 1
}

/** Operators whose operand is a LIST of any length, not a fixed count. */
function isMulti(op: FilterOperator): boolean {
  return op === 'in' || op === 'not in'
}

/**
 * Draft rows → the conditions actually applied: drop the incomplete ones and
 * keep each operator's operands.
 *
 * `in` / `not in` carry a LIST, so they keep EVERY selected value. Trimming them
 * to `operandCount` (1) — as this once did for all operators alike — silently
 * turned "is any of East, West" into "= East": a filter that ran, returned rows,
 * and was wrong. Exported for the regression tests.
 */
export function normalizeFilterRows(rows: FilterCondition[]): FilterCondition[] {
  return rows.filter((row) => {
    if (!row.column) return false
    const need = operandCount(row.operator)
    if (need === 0) return true
    const vals = row.values || []
    if (isMulti(row.operator)) return vals.length > 0
    for (let i = 0; i < need; i += 1) {
      const v = vals[i]
      if (v === undefined || v === null || (typeof v === 'string' && v.trim() === '')) return false
    }
    return true
  }).map((row) => ({
    ...row,
    values: isMulti(row.operator)
      ? [...(row.values || [])]
      : (row.values || []).slice(0, Math.max(1, operandCount(row.operator))),
  }))
}

/**
 * The value editor: a COMBO BOX — type a value or pick one of the column's real
 * values, in the same control.
 *
 * The two used to be exclusive: a column with a picklist rendered a `<select>`
 * and NOTHING could be typed, which made a value absent from the loaded rows
 * unfilterable and made `contains` / `starts with` nonsensical (you were offered
 * whole values when you wanted a fragment). A column without one rendered a bare
 * input with no idea what the real values were.
 *
 * Multi-value operators (`is any of` / `is none of`) hold their selection as
 * removable CHIPS rather than a ctrl-click `<select multiple>`, so what is
 * selected is visible without scrolling and each value can be dropped on its
 * own. The old free-text fallback for those split on commas, which silently
 * mangled any value containing one; chips are committed one at a time instead.
 */
function ValueCombo({
  values, index = 0, multi, options, placeholder, onChange, ariaLabel,
}: {
  values: unknown[]
  /** Operand slot this field edits (0, or 1 for the upper bound of `between`). */
  index?: number
  multi: boolean
  options?: ValueOption[]
  placeholder?: string
  onChange: (values: unknown[]) => void
  ariaLabel?: string
}) {
  const displayFor = (raw: unknown) =>
    options?.find((o) => String(o.raw ?? '') === String(raw ?? ''))?.display
    ?? (raw === undefined || raw === null ? '' : String(raw))

  const committed = multi ? '' : displayFor(values[index])
  const [text, setText] = useState(committed)
  const [open, setOpen] = useState(false)
  const [hi, setHi] = useState(0)
  const boxRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  // Re-sync when the value changes from OUTSIDE (operator switched, dialog
  // reopened). Tracking the last committed text — not the value — keeps the
  // user's in-progress typing from being overwritten on every parent render.
  const lastCommitted = useRef(committed)
  if (lastCommitted.current !== committed) {
    lastCommitted.current = committed
    if (!multi && text !== committed) setText(committed)
  }

  const suggestions = matchValueOptions(options, text, multi ? values : [])
  const hasOptions = Boolean(options?.length)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const setSingle = (raw: unknown) => {
    const next = [...values]
    next[index] = raw
    onChange(next)
  }
  const addChip = (raw: unknown) => {
    if (raw === undefined || raw === null || String(raw).trim() === '') return
    if (values.some((v) => String(v ?? '') === String(raw))) return
    onChange([...values, raw])
  }
  const pick = (opt: ValueOption) => {
    if (multi) {
      addChip(opt.raw)
      setText('')
    } else {
      setSingle(opt.raw)
      setText(opt.display)
      lastCommitted.current = opt.display
      setOpen(false)
    }
    setHi(0)
    inputRef.current?.focus()
  }
  /** Enter: take the highlighted suggestion when one is showing, else the typed
   *  text verbatim — a value the loaded rows don't contain is still valid. */
  const commitTyped = () => {
    const hit = suggestions[hi]
    if (hit && text.trim() !== '') { pick(hit); return }
    if (multi) { addChip(text.trim()); setText('') } else { setOpen(false) }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault(); setOpen(true); setHi((i) => Math.min(i + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); setHi((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault(); commitTyped()
    } else if (e.key === 'Escape' && open) {
      e.preventDefault(); e.stopPropagation(); setOpen(false)
    } else if (e.key === 'Backspace' && multi && text === '' && values.length) {
      onChange(values.slice(0, -1))
    }
  }

  const fieldStyle = {
    flex: 1, minWidth: 0, position: 'relative' as const,
  }
  return (
    <div ref={boxRef} style={fieldStyle}>
      <div
        onClick={() => inputRef.current?.focus()}
        style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3,
          minHeight: 28, padding: '2px 4px', border: BORDER, borderRadius: 4,
          background: 'var(--color-bg, #fff)', cursor: 'text',
        }}
      >
        {multi && values.map((v, i) => (
          <span
            key={`${String(v)}-${i}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%',
              padding: '1px 4px 1px 7px', borderRadius: 10, fontSize: 11,
              border: `1px solid ${ACCENT}`, color: 'var(--color-text, #3a4a6b)',
              ...softAccent(0.10),
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayFor(v)}
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(values.filter((_, j) => j !== i)) }}
              aria-label={`Remove ${displayFor(v)}`}
              title={`Remove ${displayFor(v)}`}
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer', padding: 0,
                lineHeight: 1, fontSize: 11, color: 'var(--color-text-secondary, #6b7280)',
              }}
            >
              ✕
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          value={text}
          placeholder={multi && values.length ? 'Add another…' : placeholder}
          onChange={(e) => {
            setText(e.target.value)
            setHi(0)
            setOpen(true)
            // A single-value field commits as you type, so Apply works without
            // pressing Enter first — the chip fields cannot (a half-typed value
            // is not a selection yet).
            if (!multi) { setSingle(e.target.value); lastCommitted.current = e.target.value }
          }}
          onFocus={() => { if (hasOptions) setOpen(true) }}
          onKeyDown={onKeyDown}
          style={{
            flex: 1, minWidth: 60, border: 'none', outline: 'none', height: 22,
            background: 'transparent', color: 'var(--color-text, #3a4a6b)', font: FONT,
          }}
        />
        {hasOptions && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); inputRef.current?.focus() }}
            aria-label="Show values"
            title="Show this column’s values"
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 2px',
              color: 'var(--color-text-secondary, #6b7280)', fontSize: 10, lineHeight: 1,
            }}
          >
            ▾
          </button>
        )}
      </div>

      {open && hasOptions && (
        <div
          role="listbox"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, marginTop: 2,
            maxHeight: 190, overflowY: 'auto', border: BORDER, borderRadius: 4,
            background: 'var(--color-bg, #fff)', boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
          }}
        >
          {suggestions.length === 0 && (
            <div style={{ padding: '6px 8px', color: 'var(--color-text-secondary, #6b7280)' }}>
              No matching value — press Enter to use “{text.trim()}” anyway.
            </div>
          )}
          {suggestions.map((opt, i) => (
            <div
              key={`${String(opt.raw)}-${i}`}
              role="option"
              aria-selected={i === hi}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(opt) }}
              style={{
                padding: '4px 8px', cursor: 'pointer',
                ...(i === hi ? softAccent(0.14) : { backgroundColor: 'var(--color-bg, #fff)' }),
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {opt.display}
            </div>
          ))}
          {suggestions.length >= MAX_SUGGESTIONS && (
            <div style={{ padding: '4px 8px', color: 'var(--color-text-secondary, #6b7280)', fontSize: 11 }}>
              Showing the first {MAX_SUGGESTIONS} — keep typing to narrow.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export interface VisualFilterDialogProps {
  open: boolean
  onClose: () => void
  /** The visual's data — drives the column list and the value picklists. */
  jsonData: VisualJsonData
  visualLayout: VisualLayout
  /** The expression currently in force (null = unfiltered). */
  value: FilterExpression | null
  /** Called with the new expression on Apply (null when every row was removed). */
  onChange: (expr: FilterExpression | null) => void
  /** Dialog heading (defaults to "Filter this visual"). */
  title?: string
}

export function VisualFilterDialog({
  open, onClose, jsonData, visualLayout, value, onChange, title = 'Filter this visual',
}: VisualFilterDialogProps) {
  // Columns the visual can filter on: every mapped column, plus any data column
  // the layout doesn't map (so a hidden-but-returned column is still filterable).
  const { columns, distinct, rowCount } = useMemo(() => {
    const md = mapVisualData(jsonData, visualLayout)
    const byName = new Map<string, MappedColumn>()
    for (const group of Object.values(md.groups)) {
      for (const c of group) {
        if (c.isCalc) continue
        if (!byName.has(c.name)) byName.set(c.name, c)
      }
    }
    for (const key of Object.keys(md.schema)) {
      if (key === 'id' || byName.has(key)) continue
      byName.set(key, { name: key, label: humanizeColumnLabel(key), type: md.schema[key]?.type })
    }
    const cols = [...byName.values()]
    // Distinct values per column, from the loaded rows, in first-seen order.
    const values = new Map<string, ValueOption[]>()
    // A measure does not live only in the "values" group — a map colours by
    // `color`, sizes by `size`, weighs a heat layer by `weight`. Membership is
    // not enough on its own either (`color` legitimately carries a categorical
    // series), so a column counts as a measure only when it also LOOKS like one:
    // it carries an aggregate, or its values are numeric. Same rule as
    // quickFilter.quickFilterPicksFromRow, so "what is a measure" means one
    // thing across the filter feature.
    const firstRow = md.rows[0] || {}
    const looksNumeric = (v: unknown) =>
      typeof v === 'number'
      || (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)))
    const measureNames = new Set(
      pickGroup(md, 'values', 'value', 'measures', 'measure', 'size', 'weight', 'color')
        .filter((c) => Boolean(c.agg) || looksNumeric(firstRow[c.name]))
        .map((c) => c.name),
    )
    for (const col of cols) {
      // A measure's distinct list is noise (every row differs) — those columns
      // are filtered with the ordered operators, typed in.
      if (measureNames.has(col.name)) continue
      const seen = new Set<string>()
      const out: ValueOption[] = []
      for (const row of md.rows) {
        const raw = row[col.name]
        if (raw === null || raw === undefined || raw === '') continue
        const key = String(raw)
        if (seen.has(key)) continue
        seen.add(key)
        const shown = formatCell(raw, {
          type: col.type, coltype: col.coltype, format: col.format, pattern: col.pattern,
        })
        out.push({ raw, display: shown !== '' ? String(shown) : key })
        if (out.length > MAX_PICKLIST) break
      }
      if (out.length <= MAX_PICKLIST) values.set(col.name, out)
    }
    return { columns: cols, distinct: values, rowCount: md.rows.length }
  }, [jsonData, visualLayout])

  // Draft rows — the dialog edits a flat condition list (nested groups authored
  // elsewhere are preserved as a leading group, never silently dropped).
  const [condition, setCondition] = useState<'and' | 'or'>('and')
  const [rows, setRows] = useState<FilterCondition[]>([])
  const [nested, setNested] = useState<FilterExpression[]>([])

  useEffect(() => {
    if (!open) return
    const leaves: FilterCondition[] = []
    const groups: FilterExpression[] = []
    for (const rule of value?.rules || []) {
      if (isFilterGroup(rule)) groups.push(rule)
      else if (rule?.column) leaves.push({ ...rule, values: [...(rule.values || [])] })
    }
    setCondition(value?.condition === 'or' ? 'or' : 'and')
    setRows(leaves)
    setNested(groups)
  }, [open, value])

  if (!open) return null

  const colByName = (name: string) => columns.find((c) => c.name === name)

  const addRow = () => {
    const first = columns[0]
    if (!first) return
    setRows((r) => [...r, { column: first.name, operator: opsFor(first)[0], values: [] }])
  }
  const patchRow = (idx: number, patch: Partial<FilterCondition>) => {
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, ...patch } : row)))
  }
  const removeRow = (idx: number) => setRows((r) => r.filter((_, i) => i !== idx))

  const apply = () => {
    const expr: FilterExpression = {
      condition,
      rules: [...nested, ...normalizeFilterRows(rows)],
    }
    onChange(hasFilterRules(expr) ? expr : null)
    onClose()
  }

  const clearAll = () => {
    setRows([])
    setNested([])
    onChange(null)
    onClose()
  }

  const selectStyle = {
    height: 28, padding: '0 6px', border: BORDER, borderRadius: 4,
    background: 'var(--color-bg, #fff)', color: 'var(--color-text, #3a4a6b)', font: FONT,
    minWidth: 0,
  } as const

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10060, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        role="dialog"
        aria-label={title}
        style={{
          width: 'min(620px, 100%)', maxHeight: '100%', display: 'flex', flexDirection: 'column',
          background: 'var(--color-bg, #fff)', borderRadius: 10, overflow: 'hidden',
          boxShadow: '0 12px 48px rgba(0,0,0,0.35)', font: FONT,
          color: 'var(--color-text, #3a4a6b)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
          borderBottom: BORDER, background: 'var(--color-bg-secondary, #f8f9fa)', flexShrink: 0,
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{title}</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-secondary, #6b7280)' }}>
            {rowCount} row{rowCount === 1 ? '' : 's'} loaded
          </span>
          <button
            type="button" onClick={onClose} aria-label="Close" title="Close (Esc)"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, lineHeight: 1, color: 'var(--color-text-secondary, #6b7280)' }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: '12px 14px', overflow: 'auto', display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--color-text-secondary, #6b7280)' }}>Keep rows that</span>
            <select
              value={condition}
              onChange={(e) => setCondition(e.target.value === 'or' ? 'or' : 'and')}
              style={selectStyle}
            >
              <option value="and">match all conditions</option>
              <option value="or">match any condition</option>
            </select>
          </div>

          {nested.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--color-text-secondary, #6b7280)' }}>
              {nested.length} grouped condition{nested.length === 1 ? '' : 's'} set elsewhere
              {' '}are kept and combined with the rows below.
            </div>
          )}

          {rows.length === 0 && (
            <div style={{ color: 'var(--color-text-secondary, #6b7280)', fontStyle: 'italic' }}>
              No filter — this visual shows everything it loaded.
            </div>
          )}

          {rows.map((row, idx) => {
            const col = colByName(row.column)
            const ops = opsFor(col)
            const need = operandCount(row.operator)
            const picklist = distinct.get(row.column)
            const multi = isMulti(row.operator)
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <select
                  value={row.column}
                  onChange={(e) => {
                    const next = colByName(e.target.value)
                    const nextOps = opsFor(next)
                    patchRow(idx, {
                      column: e.target.value,
                      operator: nextOps.includes(row.operator) ? row.operator : nextOps[0],
                      values: [],
                    })
                  }}
                  style={{ ...selectStyle, flex: '0 1 34%' }}
                >
                  {columns.map((c) => (
                    <option key={c.name} value={c.name}>{c.label || c.name}</option>
                  ))}
                </select>
                <select
                  value={row.operator}
                  onChange={(e) => patchRow(idx, {
                    operator: e.target.value as FilterOperator, values: [],
                  })}
                  style={{ ...selectStyle, flex: '0 1 26%' }}
                >
                  {ops.map((op) => (
                    <option key={op} value={op}>{OP_LABEL[op]}</option>
                  ))}
                </select>

                {need === 0 && <span style={{ flex: 1 }} />}

                {need > 0 && (
                  <ValueCombo
                    multi={multi}
                    values={row.values || []}
                    options={picklist}
                    placeholder={multi ? 'Type or pick a value…' : 'Type or pick…'}
                    ariaLabel={`Value for ${col?.label || row.column}`}
                    onChange={(values) => patchRow(idx, { values })}
                  />
                )}

                {need === 2 && (
                  <ValueCombo
                    multi={false}
                    index={1}
                    values={row.values || []}
                    options={picklist}
                    placeholder="and…"
                    ariaLabel={`Upper bound for ${col?.label || row.column}`}
                    onChange={(values) => patchRow(idx, { values })}
                  />
                )}

                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  title="Remove this condition"
                  aria-label="Remove this condition"
                  style={{
                    border: BORDER, background: 'var(--color-bg, #fff)', borderRadius: 4,
                    cursor: 'pointer', width: 28, height: 28, flexShrink: 0,
                    color: 'var(--color-text-secondary, #6b7280)',
                  }}
                >
                  ✕
                </button>
              </div>
            )
          })}

          <div>
            <button
              type="button"
              onClick={addRow}
              disabled={columns.length === 0}
              style={{
                border: BORDER, background: 'var(--color-bg, #fff)', borderRadius: 4,
                padding: '5px 10px', cursor: columns.length ? 'pointer' : 'not-allowed',
                color: 'var(--color-text, #3a4a6b)', font: FONT,
              }}
            >
              + Add condition
            </button>
          </div>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 6, padding: '10px 14px',
          borderTop: BORDER, background: 'var(--color-bg-secondary, #f8f9fa)', flexShrink: 0,
        }}>
          <button
            type="button" onClick={onClose}
            style={{ border: BORDER, background: 'var(--color-bg, #fff)', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', font: FONT, color: 'inherit' }}
          >
            Cancel
          </button>
          <button
            type="button" onClick={clearAll}
            style={{ border: BORDER, background: 'var(--color-bg, #fff)', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', font: FONT, color: 'inherit' }}
          >
            Clear all
          </button>
          <button
            type="button" onClick={apply}
            style={{
              border: '1px solid var(--pg-primary, #1976d2)', background: 'var(--pg-primary, #1976d2)',
              color: '#fff', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', font: FONT,
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  )
}

export default VisualFilterDialog
